// URL rules for website reverse engineering. Pure and client-safe: the form validates with the same
// function the server uses before any DNS lookup (the server then checks the resolved addresses).
import { SITE_URL_MAX } from './types'

export type SiteUrlError = 'empty' | 'too_long' | 'invalid_url' | 'unsupported_scheme' | 'credentials_in_url' | 'port_not_allowed' | 'ip_not_allowed' | 'host_not_public'

export interface NormalizedSiteUrl {
  href: string
  origin: string
  /** lowercase ASCII hostname (IDN already punycode) */
  host: string
}

/** Names that never resolve to a public website (RFC 2606/6761/8375 and common private zones). */
const PRIVATE_SUFFIXES = ['localhost', 'local', 'internal', 'intranet', 'lan', 'home', 'corp', 'private', 'home.arpa', 'test', 'example', 'invalid', 'onion', 'localdomain']
const TRACKING = /^(utm_[a-z]+|gclid|fbclid|msclkid|mc_cid|mc_eid|_hs(enc|mi)|yclid|igshid|ref_src)$/i

export class SiteUrlProblem extends Error {
  constructor(public code: SiteUrlError) {
    super(code)
    this.name = 'SiteUrlProblem'
  }
}

const isIpLiteral = (host: string) => /^\[.*\]$/.test(host) || /^\d{1,3}(\.\d{1,3}){3}$/.test(host) || /^0x[0-9a-f]+$/i.test(host) || /^\d+$/.test(host)

/** Throws SiteUrlProblem; returns the canonical URL otherwise. `example.com` becomes `https://example.com/`. */
export function normalizeSiteUrl(input: string): NormalizedSiteUrl {
  const raw = (input ?? '').trim()
  if (!raw) throw new SiteUrlProblem('empty')
  if (raw.length > SITE_URL_MAX) throw new SiteUrlProblem('too_long')
  if (/\s/.test(raw)) throw new SiteUrlProblem('invalid_url')
  // "example.com:8080" and "localhost:3000" are host:port, not a scheme; "mailto:", "data:" … are schemes
  const authority = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw)
  if (!authority && /^(mailto|javascript|vbscript|data|file|ftp|tel|sms|blob|about|chrome|view-source|ws|wss|gopher|ldap|dict|sftp|ssh|smb|urn|news|irc):/i.test(raw)) throw new SiteUrlProblem('unsupported_scheme')
  const withScheme = authority ? raw : raw.startsWith('//') ? `https:${raw}` : `https://${raw}`
  let url: URL
  try {
    url = new URL(withScheme)
  } catch {
    throw new SiteUrlProblem('invalid_url')
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') throw new SiteUrlProblem('unsupported_scheme')
  if (url.username || url.password) throw new SiteUrlProblem('credentials_in_url')
  if (url.port && !((url.protocol === 'http:' && url.port === '80') || (url.protocol === 'https:' && url.port === '443'))) throw new SiteUrlProblem('port_not_allowed')
  const host = url.hostname.toLowerCase().replace(/\.$/, '')
  if (!host) throw new SiteUrlProblem('invalid_url')
  if (isIpLiteral(host) || isIpLiteral(url.hostname)) throw new SiteUrlProblem('ip_not_allowed')
  if (!host.includes('.') || host.length > 253) throw new SiteUrlProblem('host_not_public')
  const labels = host.split('.')
  if (labels.some((l) => !l || l.length > 63 || !/^[a-z0-9_-]+$/.test(l) || l.startsWith('-') || l.endsWith('-'))) throw new SiteUrlProblem('invalid_url')
  const tld = labels.at(-1)!
  if (/^\d+$/.test(tld) || tld.length < 2) throw new SiteUrlProblem('host_not_public')
  if (PRIVATE_SUFFIXES.some((s) => host === s || host.endsWith(`.${s}`))) throw new SiteUrlProblem('host_not_public')
  url.hash = ''
  for (const key of [...url.searchParams.keys()]) if (TRACKING.test(key)) url.searchParams.delete(key)
  url.hostname = host
  return { href: url.toString(), origin: url.origin, host }
}

/** Same-code check used by the form: null when the URL is acceptable. */
export function siteUrlError(input: string): SiteUrlError | null {
  try {
    normalizeSiteUrl(input)
    return null
  } catch (e) {
    return e instanceof SiteUrlProblem ? e.code : 'invalid_url'
  }
}

export const bareHost = (host: string) => host.toLowerCase().replace(/^www\d?\./, '')

/** 'same' = the same site (www. or not), 'sub' = a subdomain of it (docs., app.), 'other' = elsewhere. */
export function siteRelation(baseHost: string, host: string): 'same' | 'sub' | 'other' {
  const a = bareHost(baseHost), b = bareHost(host)
  if (a === b) return 'same'
  if (b.endsWith(`.${a}`)) return 'sub'
  return 'other'
}

/** Dedupe key: host without www., path without trailing slash, no query/hash; lowercase. */
export function pageKey(href: string): string {
  try {
    const u = new URL(href)
    const path = decodeURIComponent(u.pathname).replace(/\/+$/, '').toLowerCase() || '/'
    return `${bareHost(u.hostname)}${path}`
  } catch {
    return href
  }
}

/** Resolve a link against its page; only http(s), no fragments, tracking removed. null = not a page link. */
export function resolveLink(href: string, base: string): string | null {
  const h = (href ?? '').trim()
  if (!h || h.startsWith('#') || /^(mailto|tel|javascript|data|blob|sms|whatsapp|intent|ftp|file):/i.test(h)) return null
  try {
    const u = new URL(h, base)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null
    if (u.username || u.password) return null
    u.hash = ''
    for (const key of [...u.searchParams.keys()]) if (TRACKING.test(key)) u.searchParams.delete(key)
    return u.toString()
  } catch {
    return null
  }
}
